import React, { useState, useEffect, useContext, useMemo } from "react";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  CustomField,
  CustomFieldRequest,
  CustomFieldDataType,
  CustomFieldChoiceOption,
  CustomFieldDependencyLevel,
  CustomFieldService,
} from "./customFields.service";
import { AuthContext } from "../../../context/authcontext";
import { showSuccessAlert } from "../../uiInterface/base-ui/alertservice";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";

const MySwal = withReactContent(Swal);

const convertTo12HourFormat = (time24: string): string => {
  if (!time24) return "";

  const timeParts = time24.split(":");
  const hours = timeParts[0];
  const minutes = timeParts[1];
  const seconds = timeParts[2] || "00";

  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;

  return `${hour12}:${minutes}:${seconds} ${ampm}`;
};

const convertTo24HourFormat = (time12: string): string => {
  if (!time12) return "";

  const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;
  const match = time12.match(timeRegex);

  if (!match) return time12;

  let [, hours, minutes, seconds, ampm] = match;
  let hour = parseInt(hours, 10);

  if (ampm.toUpperCase() === "PM" && hour !== 12) {
    hour += 12;
  } else if (ampm.toUpperCase() === "AM" && hour === 12) {
    hour = 0;
  }

  const secondsPart = seconds ? `:${seconds}` : "";
  return `${hour.toString().padStart(2, "0")}:${minutes}${secondsPart}`;
};

const convertDateTimeTo12HourFormat = (datetime: string): string => {
  if (!datetime) return "";

  try {
    const date = new Date(datetime);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    return `${year}-${month}-${day} ${hours
      .toString()
      .padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
  } catch {
    return datetime;
  }
};

const convertDateTimeFrom12HourFormat = (datetime12: string): string => {
  if (!datetime12) return "";

  const datetimeRegex =
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;
  const match = datetime12.match(datetimeRegex);

  if (!match) return datetime12;

  let [, year, month, day, hours, minutes, seconds, ampm] = match;
  let hour = parseInt(hours, 10);

  if (ampm.toUpperCase() === "PM" && hour !== 12) {
    hour += 12;
  } else if (ampm.toUpperCase() === "AM" && hour === 12) {
    hour = 0;
  }

  const secondsPart = seconds ? `:${seconds}` : ":00";
  return `${year}-${month}-${day}T${hour
    .toString()
    .padStart(2, "0")}:${minutes}${secondsPart}`;
};

const combineDateAndTime = (date: string, time: string): string => {
  if (!date || !time) return "";

  const timeWithSeconds = time.length === 5 ? `${time}:00` : time;
  return `${date}T${timeWithSeconds}`;
};

const extractDateFromDateTime = (datetime: string): string => {
  if (!datetime) return "";
  return datetime.split("T")[0] || "";
};

const extractTimeFromDateTime = (datetime: string): string => {
  if (!datetime) return "";
  const timePart = datetime.split("T")[1] || "";

  if (timePart.length >= 8) {
    return timePart.substring(0, 8);
  } else if (timePart.length >= 5) {
    return timePart.substring(0, 5) + ":00";
  }
  return "";
};

interface CustomFieldFormProps {
  customField?: CustomField;
  dataTypes?: CustomFieldDataType[];
  onSubmit: (
    formData: Partial<CustomFieldRequest>,
    localEntities?: string[]
  ) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  selectedEntity: string;
  selectedEntities?: string[];
  dataTypesLoading?: boolean;
  isLoadingCustomField?: boolean;
}

const CustomFieldForm: React.FC<CustomFieldFormProps> = ({
  customField,
  dataTypes,
  onSubmit,
  onCancel,
  isSubmitting,
  selectedEntity,
  selectedEntities = [],
  dataTypesLoading = false,
  isLoadingCustomField = false,
}) => {
  const { auth, lifecycleTypeNames } = useContext(AuthContext);

  const ENTITY_OPTIONS = useMemo(
    () => [
      { value: "company", label: "Company", icon: "ti ti-building-community" },
      { value: "contact", label: "Contact", icon: "ti ti-user-up" },
      { value: "lifecycle", label: "Lifecycle", icon: "ti ti-chart-arcs" },
      {
        value: "contract",
        label: getLifecycleTypeName("CONTRACT"),
        icon: "ti ti-file-certificate",
      },
    ],
    []
  );

  const getFieldTypeIcon = (dataTypeKey: string): string => {
    const key = dataTypeKey?.toUpperCase();
    const iconMap: Record<string, string> = {
      TEXT: "ti ti-text-size",
      TEXTAREA: "ti ti-align-left",
      EMAIL: "ti ti-mail",
      URL: "ti ti-link",
      PHONE: "ti ti-phone",
      DATE: "ti ti-calendar",
      TIME: "ti ti-clock",
      DATETIME: "ti ti-calendar-time",
      DATE_RANGE: "ti ti-calendar-event",
      TIME_RANGE: "ti ti-clock-hour-4",
      DATE_TIME_RANGE: "ti ti-calendar-stats",
      INTEGER: "ti ti-123",
      INTEGER_VALUES: "ti ti-123",
      INTEGER_SMALL: "ti ti-123",
      INTEGER_LARGE: "ti ti-123",
      SMALL_INTEGER: "ti ti-123",
      SMALL_INTEGER_VALUES: "ti ti-123",
      SMALLINT: "ti ti-123",
      LARGE_INTEGER: "ti ti-123",
      LARGE_INTEGER_VALUES: "ti ti-123",
      BIGINT: "ti ti-123",
      DECIMAL: "ti ti-decimal",
      DECIMAL_VALUE: "ti ti-decimal",
      DECIMAL_VALUES: "ti ti-decimal",
      NUMBER: "ti ti-hash",
      FLOAT: "ti ti-decimal",
      FLOAT_VALUES: "ti ti-decimal",
      NUMERIC: "ti ti-hash",
      DOUBLE: "ti ti-decimal",
      DOUBLE_VALUES: "ti ti-decimal",
      DROPDOWN: "ti ti-chevron-down",
      RADIO_OPTION: "ti ti-circle-dot",
      MULTIPLE_OPTION: "ti ti-checkbox",
      SWITCH: "ti ti-toggle-right",
      BOOLEAN: "ti ti-toggle-right",
      FILE: "ti ti-file",
      IMAGE: "ti ti-photo",
      DEPENDENT_FIELD: "ti ti-hierarchy-3",
      CURRENCY: "ti ti-currency-dollar",
      PERCENTAGE: "ti ti-percentage",
      RATING: "ti ti-star",
      COLOR: "ti ti-color-swatch",
      JSON: "ti ti-code",
    };
    return iconMap[key] || "ti ti-forms";
  };
  const [formData, setFormData] = useState<Partial<CustomFieldRequest>>({
    related_entity_name: selectedEntity || "",
    custom_field_display_name: "",
    custom_field_label: "",
    custom_field_description: "",
    custom_field_data_type_id: 0,
    default_value: "" as any,
    is_required: false,
    is_active: true,
    choice_options: [],
  });

  const [choiceOptions, setChoiceOptions] = useState<
    Partial<CustomFieldChoiceOption>[]
  >([]);
  const [dependencyLevels, setDependencyLevels] = useState<
    Partial<CustomFieldDependencyLevel>[]
  >([]);
  const [dependentFieldDefaultValue, setDependentFieldDefaultValue] = useState<
    Record<string, string>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [localSelectedEntities, setLocalSelectedEntities] =
    useState<string[]>(selectedEntities);

  useEffect(() => {
    const loadDependentFieldData = async () => {
      if (customField) {
        const isDependent =
          customField.custom_field_data_type?.custom_field_data_type_key ===
          "DEPENDENT_FIELD";

        if (isDependent && customField.custom_field_id) {
          try {
            const [levels, options] = await Promise.all([
              CustomFieldService.getDependencyLevelsByCustomFieldId(
                customField.custom_field_id
              ),
              CustomFieldService.getChoiceOptionsByCustomFieldId(
                customField.custom_field_id
              ),
            ]);

            if (levels && levels.length > 0) {
              setDependencyLevels(levels);
            }
            if (options && options.length > 0) {
              setChoiceOptions(options);
            }

            if (customField.default_value) {
              let defaultValueObj = customField.default_value;
              if (typeof defaultValueObj === "string") {
                try {
                  defaultValueObj = JSON.parse(defaultValueObj);
                } catch {
                  defaultValueObj = {};
                }
              }

              if (typeof defaultValueObj === "object" && defaultValueObj) {
                setDependentFieldDefaultValue(defaultValueObj);
              }
            }
          } catch (error) {
            console.error("Error loading dependent field data:", error);
          }
        }
      }
    };

    if (customField) {
      const dataTypeId =
        customField.custom_field_data_type?.custom_field_data_type_id ||
        customField.custom_field_data_type_id;

      const isMultipleOption =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "MULTIPLE_OPTION";
      const isDateRange =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "DATE_RANGE";
      const isTimeRange =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "TIME_RANGE";
      const isDateTimeRange =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "DATE_TIME_RANGE";
      const isTime =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "TIME";
      const isDateTime =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "DATETIME";
      const isDependentField =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "DEPENDENT_FIELD";
      const isNumeric =
        customField.custom_field_data_type &&
        [
          "INTEGER",
          "INTEGER_VALUES",
          "INTEGER_SMALL",
          "INTEGER_LARGE",
          "SMALL_INTEGER",
          "SMALL_INTEGER_VALUES",
          "SMALLINT",
          "LARGE_INTEGER",
          "LARGE_INTEGER_VALUES",
          "BIGINT",
          "DECIMAL",
          "DECIMAL_VALUE",
          "DECIMAL_VALUES",
          "NUMBER",
          "FLOAT",
          "FLOAT_VALUES",
          "NUMERIC",
          "DOUBLE",
          "DOUBLE_VALUES",
        ].includes(
          customField.custom_field_data_type.custom_field_data_type_key?.toUpperCase()
        );
      let defaultValue = customField.default_value || "";

      if (isNumeric && typeof defaultValue === "number") {
        defaultValue = defaultValue.toString();
      } else if (isDependentField) {
        if (typeof defaultValue === "string" && defaultValue) {
          try {
            const parsedDefaultValue = JSON.parse(defaultValue);
            setDependentFieldDefaultValue(parsedDefaultValue || {});
          } catch {
            setDependentFieldDefaultValue({});
          }
        } else if (typeof defaultValue === "object" && defaultValue) {
          setDependentFieldDefaultValue(defaultValue);
        } else {
          setDependentFieldDefaultValue({});
        }
        defaultValue = "";
      } else if (
        isMultipleOption &&
        typeof defaultValue === "string" &&
        defaultValue
      ) {
        try {
          defaultValue = JSON.parse(defaultValue);
        } catch {
          defaultValue = [defaultValue];
        }
      } else if (isMultipleOption && !Array.isArray(defaultValue)) {
        defaultValue = defaultValue ? [defaultValue] : [];
      } else if (
        (isDateRange || isTimeRange || isDateTimeRange) &&
        typeof defaultValue === "string" &&
        defaultValue
      ) {
        try {
          const parsedValue = JSON.parse(defaultValue);
          if (isTimeRange) {
            defaultValue = {
              start: parsedValue.start_time
                ? convertTo24HourFormat(parsedValue.start_time)
                : "",
              end: parsedValue.end_time
                ? convertTo24HourFormat(parsedValue.end_time)
                : "",
            };
          } else if (isDateRange) {
            defaultValue = {
              start: parsedValue.start_date || "",
              end: parsedValue.end_date || "",
            };
          } else if (isDateTimeRange) {
            defaultValue = {
              start: parsedValue.start_datetime || "",
              end: parsedValue.end_datetime || "",
            };
          } else {
            defaultValue = parsedValue;
          }
        } catch {
          defaultValue = { start: "", end: "" };
        }
      } else if (
        (isDateRange || isTimeRange || isDateTimeRange) &&
        typeof defaultValue !== "object"
      ) {
        defaultValue = { start: "", end: "" };
      } else if (isTime && typeof defaultValue === "string" && defaultValue) {
        defaultValue = convertTo24HourFormat(defaultValue);
      } else if (
        isDateTime &&
        typeof defaultValue === "string" &&
        defaultValue
      ) {
        defaultValue = convertDateTimeFrom12HourFormat(defaultValue);
      }

      const isSwitch =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "SWITCH";

      if (isSwitch) {
        if (defaultValue === true || defaultValue === "true" || defaultValue === "TRUE") {
          defaultValue = "TRUE";
        } else if (defaultValue === false || defaultValue === "false" || defaultValue === "FALSE") {
          defaultValue = "FALSE";
        } else {
          defaultValue = "";
        }
      }

      setFormData({
        related_entity_name: customField.related_entity_name,
        custom_field_display_name: customField.custom_field_display_name,
        custom_field_label: customField.custom_field_label,
        custom_field_description: customField.custom_field_description || "",
        custom_field_data_type_id: dataTypeId,
        default_value: defaultValue,
        is_required: customField.is_required,
        is_active: customField.is_active,
      });

      const isDependent =
        customField.custom_field_data_type?.custom_field_data_type_key ===
        "DEPENDENT_FIELD";

      if (!isDependent && customField.choice_options) {
        setChoiceOptions(
          customField.choice_options.map((option) => ({
            custom_field_choice_option_id: option.custom_field_choice_option_id,
            choice_label: option.choice_label,
            display_order: option.display_order,
          }))
        );
      } else if (!isDependent) {
        setChoiceOptions([]);
      }

      loadDependentFieldData();
    } else {
      setChoiceOptions([]);
      setDependencyLevels([]);
      setDependentFieldDefaultValue({});
    }
  }, [customField]);

  useEffect(() => {
    if (formData.custom_field_display_name) {
      const label = formData.custom_field_display_name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");

      setFormData((prev) => ({
        ...prev,
        custom_field_label: label,
      }));
    }
  }, [formData.custom_field_display_name]);

  useEffect(() => {
    if (selectedEntity && !customField) {
      setFormData((prev) => ({
        ...prev,
        related_entity_name: selectedEntity,
      }));
    }
  }, [selectedEntity, customField]);

  useEffect(() => {
    setLocalSelectedEntities(selectedEntities);
  }, [selectedEntities]);

  const selectedDataType =
    dataTypes?.find(
      (type) =>
        type.custom_field_data_type_id === formData.custom_field_data_type_id
    ) || customField?.custom_field_data_type;

  const isChoiceType =
    selectedDataType &&
    ["DROPDOWN", "RADIO_OPTION", "MULTIPLE_OPTION"].includes(
      selectedDataType.custom_field_data_type_key
    );

  const isMultipleOptionType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "MULTIPLE_OPTION";

  const isSwitchType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "SWITCH";

  const isDateType =
    selectedDataType && selectedDataType.custom_field_data_type_key === "DATE";

  const isTimeType =
    selectedDataType && selectedDataType.custom_field_data_type_key === "TIME";

  const isDateTimeType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "DATETIME";

  const isDateRangeType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "DATE_RANGE";

  const isTimeRangeType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "TIME_RANGE";

  const isDateTimeRangeType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "DATE_TIME_RANGE";

  const isDependentFieldType =
    selectedDataType &&
    selectedDataType.custom_field_data_type_key === "DEPENDENT_FIELD";

  const isNumericType =
    selectedDataType &&
    [
      "INTEGER",
      "INTEGER_VALUES",
      "INTEGER_SMALL",
      "INTEGER_LARGE",
      "SMALL_INTEGER",
      "SMALL_INTEGER_VALUES",
      "SMALLINT",
      "LARGE_INTEGER",
      "LARGE_INTEGER_VALUES",
      "BIGINT",
      "DECIMAL",
      "DECIMAL_VALUE",
      "DECIMAL_VALUES",
      "NUMBER",
      "FLOAT",
      "FLOAT_VALUES",
      "NUMERIC",
      "DOUBLE",
      "DOUBLE_VALUES",
    ].includes(selectedDataType.custom_field_data_type_key?.toUpperCase());

  const isIntegerType =
    selectedDataType &&
    [
      "INTEGER",
      "INTEGER_VALUES",
      "INTEGER_SMALL",
      "INTEGER_LARGE",
      "SMALL_INTEGER",
      "SMALL_INTEGER_VALUES",
      "SMALLINT",
      "LARGE_INTEGER",
      "LARGE_INTEGER_VALUES",
      "BIGINT",
    ].includes(selectedDataType.custom_field_data_type_key?.toUpperCase());

  const isFieldTypeSelected = !!selectedDataType;

  const availableChoiceOptions = choiceOptions.filter((option) =>
    option.choice_label?.trim()
  );

  useEffect(() => {
    if (isChoiceType && formData.default_value) {
      if (isMultipleOptionType && Array.isArray(formData.default_value)) {
        const validValues = formData.default_value.filter((value) =>
          availableChoiceOptions.some(
            (option) =>
              option.choice_label === value ||
              option.custom_field_choice_option_id?.toString() ===
              value?.toString()
          )
        );

        if (validValues.length !== formData.default_value.length) {
          setFormData((prev) => ({
            ...prev,
            default_value: validValues.length > 0 ? validValues : [],
          }));
        }
      } else if (!isMultipleOptionType) {
        const isDefaultValueStillAvailable = availableChoiceOptions.some(
          (option) =>
            option.choice_label === formData.default_value ||
            option.custom_field_choice_option_id?.toString() ===
            formData.default_value?.toString()
        );

        if (!isDefaultValueStillAvailable) {
          setFormData((prev) => ({
            ...prev,
            default_value: "",
          }));
        }
      }
    }
  }, [
    choiceOptions,
    isChoiceType,
    isMultipleOptionType,
    formData.default_value,
    availableChoiceOptions,
  ]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.custom_field_display_name?.trim()) {
      newErrors.custom_field_display_name = "Display name is required";
    }

    if (!customField && localSelectedEntities.length === 0) {
      newErrors.related_entity_name = "At least one entity is required";
    }

    if (!formData.custom_field_data_type_id) {
      newErrors.custom_field_data_type_id = "Field type is required";
    }

    if (isChoiceType && !isDependentFieldType && choiceOptions.length === 0) {
      newErrors.choice_options =
        "At least one option is required for choice fields";
    }

    if (isDependentFieldType) {
      if (dependencyLevels.length === 0) {
        newErrors.dependency_level =
          "At least one dependency level is required";
      }
      if (choiceOptions.length === 0) {
        newErrors.choice_options =
          "At least one option is required for dependent fields";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveAllPendingChanges = async () => {
    if (!customField || !isDependentFieldType) return;

    const savePromises: Promise<any>[] = [];

    for (let i = 0; i < dependencyLevels.length; i++) {
      const level = dependencyLevels[i];
      if (!level.dependency_level?.trim()) continue;

      try {
        if (
          level.custom_field_dependency_level_id &&
          level.custom_field_dependency_level_id > 0
        ) {
          const promise = CustomFieldService.updateDependencyLevel(
            level.custom_field_dependency_level_id,
            {
              dependency_level: level.dependency_level,
              display_order: level.display_order,
            },
            customField.tenant_id
          );
          savePromises.push(promise);
        } else {
          const promise = CustomFieldService.createDependencyLevel(
            {
              custom_field_id: customField.custom_field_id,
              dependency_level: level.dependency_level,
              display_order: level.display_order,
              created_by: auth?.user_id,
              updated_by: auth?.user_id,
            },
            customField.tenant_id
          ).then((newLevel) => {
            setDependencyLevels((prev) =>
              prev.map((l, idx) =>
                idx === i
                  ? {
                    ...l,
                    custom_field_dependency_level_id:
                      newLevel.custom_field_dependency_level_id,
                  }
                  : l
              )
            );
            return newLevel;
          });
          savePromises.push(promise);
        }
      } catch (error) {
        console.error("Error saving dependency level:", error);
      }
    }

    for (let i = 0; i < choiceOptions.length; i++) {
      const option = choiceOptions[i];
      if (!option.choice_label?.trim()) continue;

      try {
        if (
          option.custom_field_choice_option_id &&
          option.custom_field_choice_option_id > 0
        ) {
          const promise = CustomFieldService.updateChoiceOption(
            option.custom_field_choice_option_id,
            {
              choice_label: option.choice_label,
              display_order: option.display_order,
              custom_field_dependency_level_id:
                option.custom_field_dependency_level_id,
              parent_choice_option_id: option.parent_choice_option_id,
            },
            customField.tenant_id
          );
          savePromises.push(promise);
        } else {
          const promise = CustomFieldService.createChoiceOption(
            {
              custom_field_id: customField.custom_field_id,
              choice_label: option.choice_label,
              display_order: option.display_order,
              custom_field_dependency_level_id:
                option.custom_field_dependency_level_id,
              parent_choice_option_id: option.parent_choice_option_id,
              created_by: auth?.user_id,
              updated_by: auth?.user_id,
            },
            customField.tenant_id
          ).then((newOption) => {
            setChoiceOptions((prev) =>
              prev.map((opt, idx) =>
                idx === i
                  ? {
                    ...opt,
                    custom_field_choice_option_id:
                      newOption.custom_field_choice_option_id,
                  }
                  : opt
              )
            );
            return newOption;
          });
          savePromises.push(promise);
        }
      } catch (error) {
        console.error("Error saving choice option:", error);
      }
    }

    if (savePromises.length > 0) {
      try {
        await Promise.all(savePromises);
      } catch (error) {
        console.error("Error saving pending changes:", error);
        throw error;
      }
    }
  };

  const saveChoiceOptions = async () => {
    if (!customField || !isChoiceType || isDependentFieldType) return;

    const savePromises: Promise<any>[] = [];

    for (let i = 0; i < choiceOptions.length; i++) {
      const option = choiceOptions[i];
      if (!option.choice_label?.trim()) continue;

      try {
        if (
          option.custom_field_choice_option_id &&
          option.custom_field_choice_option_id > 0
        ) {
          const promise = CustomFieldService.updateChoiceOption(
            option.custom_field_choice_option_id,
            {
              choice_label: option.choice_label,
              display_order: option.display_order,
            },
            customField.tenant_id
          );
          savePromises.push(promise);
        } else {
          const promise = CustomFieldService.createChoiceOption(
            {
              custom_field_id: customField.custom_field_id,
              choice_label: option.choice_label,
              display_order: option.display_order,
              created_by: auth?.user_id,
              updated_by: auth?.user_id,
            },
            customField.tenant_id
          ).then((newOption) => {
            setChoiceOptions((prev) =>
              prev.map((opt, idx) =>
                idx === i
                  ? {
                    ...opt,
                    custom_field_choice_option_id:
                      newOption.custom_field_choice_option_id,
                  }
                  : opt
              )
            );
            return newOption;
          });
          savePromises.push(promise);
        }
      } catch (error) {
        console.error("Error saving choice option:", error);
        throw error;
      }
    }

    if (savePromises.length > 0) {
      try {
        await Promise.all(savePromises);
      } catch (error) {
        console.error("Error saving choice options:", error);
        throw error;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (customField && isDependentFieldType) {
      try {
        MySwal.fire({
          title: "Saving changes...",
          text: "Please wait while we save your dependency levels and options",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            MySwal.showLoading();
          },
        });

        await saveAllPendingChanges();

        MySwal.close();
      } catch (error) {
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to save dependent field changes. Please try again.",
        });
        return;
      }
    }

    if (customField && isChoiceType && !isDependentFieldType) {
      try {
        MySwal.fire({
          title: "Saving changes...",
          text: "Please wait while we save your choice options",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            MySwal.showLoading();
          },
        });

        await saveChoiceOptions();

        MySwal.close();
      } catch (error) {
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to save choice options. Please try again.",
        });
        return;
      }
    }

    let processedDefaultValue = formData.default_value;

    const dataTypeKey =
      selectedDataType?.custom_field_data_type_key?.toUpperCase();
    const numericTypeKeys = [
      "INTEGER",
      "INTEGER_VALUES",
      "INTEGER_SMALL",
      "INTEGER_LARGE",
      "SMALL_INTEGER",
      "SMALL_INTEGER_VALUES",
      "SMALLINT",
      "LARGE_INTEGER",
      "LARGE_INTEGER_VALUES",
      "BIGINT",
      "DECIMAL",
      "DECIMAL_VALUE",
      "DECIMAL_VALUES",
      "NUMBER",
      "FLOAT",
      "FLOAT_VALUES",
      "NUMERIC",
      "DOUBLE",
      "DOUBLE_VALUES",
    ];
    const isNumeric = dataTypeKey && numericTypeKeys.includes(dataTypeKey);

    if (
      isNumeric &&
      formData.default_value !== "" &&
      formData.default_value !== null &&
      formData.default_value !== undefined
    ) {
      const integerTypeKeys = [
        "INTEGER",
        "INTEGER_VALUES",
        "INTEGER_SMALL",
        "INTEGER_LARGE",
        "SMALL_INTEGER",
        "SMALL_INTEGER_VALUES",
        "SMALLINT",
        "LARGE_INTEGER",
        "LARGE_INTEGER_VALUES",
        "BIGINT",
      ];
      const isInteger = dataTypeKey && integerTypeKeys.includes(dataTypeKey);
      const numericValue = isInteger
        ? parseInt(String(formData.default_value), 10)
        : parseFloat(String(formData.default_value));

      processedDefaultValue = isNaN(numericValue) ? null : numericValue;
    } else if (
      isNumeric &&
      (formData.default_value === "" ||
        formData.default_value === null ||
        formData.default_value === undefined)
    ) {
      processedDefaultValue = null;
    } else if (isDateRangeType || isTimeRangeType || isDateTimeRangeType) {
      if (!formData.default_value || typeof formData.default_value !== "object") {
        processedDefaultValue = null;
      } else if (isTimeRangeType) {
        const startTime = formData.default_value.start
          ? convertTo12HourFormat(formData.default_value.start)
          : "";
        const endTime = formData.default_value.end
          ? convertTo12HourFormat(formData.default_value.end)
          : "";

        if (!startTime && !endTime) {
          processedDefaultValue = null;
        } else {
          const timeRange = {
            start_time: startTime,
            end_time: endTime,
          };
          processedDefaultValue = JSON.stringify(timeRange);
        }
      } else if (isDateRangeType) {
        const startDate = formData.default_value.start || "";
        const endDate = formData.default_value.end || "";

        if (!startDate && !endDate) {
          processedDefaultValue = null;
        } else {
          const dateRange = {
            start_date: startDate,
            end_date: endDate,
          };
          processedDefaultValue = JSON.stringify(dateRange);
        }
      } else if (isDateTimeRangeType) {
        const startDateTime = formData.default_value.start || "";
        const endDateTime = formData.default_value.end || "";

        if (!startDateTime && !endDateTime) {
          processedDefaultValue = null;
        } else {
          const dateTimeRange = {
            start_datetime: startDateTime,
            end_datetime: endDateTime,
          };
          processedDefaultValue = JSON.stringify(dateTimeRange);
        }
      }
    } else if (isDateType && formData.default_value) {
      processedDefaultValue = String(formData.default_value);
    } else if (isDateTimeType && formData.default_value) {
      processedDefaultValue = convertDateTimeTo12HourFormat(
        String(formData.default_value)
      );
    } else if (isTimeType && formData.default_value) {
      processedDefaultValue = convertTo12HourFormat(
        String(formData.default_value)
      );
    } else if (isSwitchType) {
      if (formData.default_value === "TRUE") {
        processedDefaultValue = true;
      } else if (formData.default_value === "FALSE") {
        processedDefaultValue = false;
      } else {
        processedDefaultValue = null;
      }
    } else if (
      isDependentFieldType &&
      Object.keys(dependentFieldDefaultValue).length > 0
    ) {
      if (customField) {
        const idBasedValue: Record<string, number> = {};
        Object.keys(dependentFieldDefaultValue).forEach((levelName) => {
          const level = dependencyLevels.find(
            (l) => l.dependency_level === levelName
          );
          const choiceLabel = dependentFieldDefaultValue[levelName];
          const choice = choiceOptions.find(
            (c) => c.choice_label === choiceLabel
          );

          if (
            level?.custom_field_dependency_level_id &&
            choice?.custom_field_choice_option_id
          ) {
            idBasedValue[level.custom_field_dependency_level_id.toString()] =
              choice.custom_field_choice_option_id;
          }
        });
        processedDefaultValue =
          Object.keys(idBasedValue).length > 0 ? idBasedValue : null;
      } else {
        const validLevelNames = dependencyLevels
          .filter((level) => level.dependency_level?.trim())
          .map((level) => level.dependency_level);

        const filteredDefaultValue: Record<string, string> = {};
        Object.keys(dependentFieldDefaultValue).forEach((key) => {
          if (
            validLevelNames.includes(key) &&
            dependentFieldDefaultValue[key]
          ) {
            filteredDefaultValue[key] = dependentFieldDefaultValue[key];
          }
        });

        processedDefaultValue =
          Object.keys(filteredDefaultValue).length > 0
            ? filteredDefaultValue
            : null;
      }
    } else if (isChoiceType && formData.default_value) {
      if (isMultipleOptionType && Array.isArray(formData.default_value)) {
        if (customField) {
          processedDefaultValue = formData.default_value
            .map((value) => {
              const option = availableChoiceOptions.find(
                (opt) =>
                  opt.custom_field_choice_option_id?.toString() ===
                  value?.toString() || opt.choice_label === value
              );
              return option?.custom_field_choice_option_id || value;
            })
            .filter(Boolean);
        } else {
          processedDefaultValue = formData.default_value
            .map((value) => {
              const option = availableChoiceOptions.find(
                (opt) => opt.choice_label === value
              );
              return option?.choice_label || value;
            })
            .filter(Boolean);
        }
      } else if (!isMultipleOptionType && customField) {
        const selectedOption = availableChoiceOptions.find(
          (option) =>
            option.custom_field_choice_option_id?.toString() ===
            formData.default_value?.toString() ||
            option.choice_label === formData.default_value
        );

        if (selectedOption?.custom_field_choice_option_id) {
          processedDefaultValue = selectedOption.custom_field_choice_option_id;
        }
      }
    }

    let processedChoiceOptions = undefined;
    let processedDependencyLevels = undefined;

    if (isDependentFieldType && !customField) {
      processedChoiceOptions = choiceOptions.map((option) => {
        const level = dependencyLevels.find(
          (l) =>
            l.custom_field_dependency_level_id ===
            option.custom_field_dependency_level_id
        );
        const parentOption = choiceOptions.find(
          (opt) =>
            opt.custom_field_choice_option_id === option.parent_choice_option_id
        );

        return {
          choice_label: option.choice_label,
          display_order: option.display_order,
          dependency_label: level?.dependency_level || "",
          parent_label: parentOption?.choice_label || "",
        };
      });

      processedDependencyLevels = dependencyLevels.map((level) => ({
        level_name: level.dependency_level,
        display_order: level.display_order,
      }));
    } else if (isChoiceType && !isDependentFieldType && !customField) {
      processedChoiceOptions = choiceOptions;
    }

    const submitData = {
      ...formData,
      related_entity_name: !customField && localSelectedEntities.length > 0
        ? localSelectedEntities[0]
        : (formData.related_entity_name || selectedEntity),
      default_value: processedDefaultValue,
      choice_options: processedChoiceOptions,
      dependency_level: processedDependencyLevels,
    };

    await onSubmit(
      submitData,
      !customField ? localSelectedEntities : undefined
    );
  };

  const addDependencyLevel = () => {
    setDependencyLevels((prev) => [
      ...prev,
      {
        custom_field_dependency_level_id: -(prev.length + 1),
        dependency_level: "",
        display_order: prev.length + 1,
      },
    ]);
  };

  const updateDependencyLevel = (
    index: number,
    field: keyof CustomFieldDependencyLevel,
    value: any
  ) => {
    setDependencyLevels((prev) =>
      prev.map((level, i) =>
        i === index ? { ...level, [field]: value } : level
      )
    );
  };

  const removeDependencyLevel = async (index: number) => {
    const levelToRemove = dependencyLevels[index];

    if (
      levelToRemove.custom_field_dependency_level_id &&
      levelToRemove.custom_field_dependency_level_id > 0
    ) {
      const result = await MySwal.fire({
        title: "Delete Dependency Level?",
        text: `Are you sure you want to delete "${levelToRemove.dependency_level}"? All options in this level will also be deleted.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await CustomFieldService.deleteDependencyLevel(
          levelToRemove.custom_field_dependency_level_id
        );
        showSuccessAlert(
          `Level "${levelToRemove.dependency_level}" deleted successfully!`
        );
      } catch (error) {
        console.error("Error deleting dependency level:", error);
        return;
      }
    }

    if (levelToRemove.custom_field_dependency_level_id) {
      setChoiceOptions((prev) =>
        prev.filter(
          (option) =>
            option.custom_field_dependency_level_id !==
            levelToRemove.custom_field_dependency_level_id
        )
      );
    }

    setDependencyLevels((prev) => {
      const newLevels = prev.filter((_, i) => i !== index);
      return newLevels.map((level, i) => ({
        ...level,
        display_order: i + 1,
      }));
    });
  };

  const moveDependencyLevel = (index: number, direction: "up" | "down") => {
    const newLevels = [...dependencyLevels];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newLevels.length) return;

    [newLevels[index], newLevels[targetIndex]] = [
      newLevels[targetIndex],
      newLevels[index],
    ];

    const updatedLevels = newLevels.map((level, i) => ({
      ...level,
      display_order: i + 1,
    }));

    setDependencyLevels(updatedLevels);
  };

  const addChoiceOption = () => {
    const newOption: Partial<CustomFieldChoiceOption> = {
      custom_field_choice_option_id: -(choiceOptions.length + 1),
      choice_label: "",
      display_order: choiceOptions.length + 1,
    };

    if (isDependentFieldType) {
      newOption.custom_field_dependency_level_id = null;
      newOption.parent_choice_option_id = null;
    }

    setChoiceOptions((prev) => [...prev, newOption]);
  };

  const updateChoiceOption = (
    index: number,
    field: keyof Omit<CustomFieldChoiceOption, "custom_field_id" | "tenant_id">,
    value: any
  ) => {
    setChoiceOptions((prev) =>
      prev.map((option, i) =>
        i === index ? { ...option, [field]: value } : option
      )
    );
  };

  const removeChoiceOption = async (index: number) => {
    const optionToRemove = choiceOptions[index];

    const isUsedAsDefault =
      formData.default_value &&
      (isMultipleOptionType && Array.isArray(formData.default_value)
        ? formData.default_value.some(
          (val) =>
            val === optionToRemove.choice_label ||
            val?.toString() ===
            optionToRemove.custom_field_choice_option_id?.toString()
        )
        : formData.default_value === optionToRemove.choice_label ||
        formData.default_value?.toString() ===
        optionToRemove.custom_field_choice_option_id?.toString());

    if (isUsedAsDefault) {
      const result = await MySwal.fire({
        title: "Option Used as Default Value",
        html: `
          <div class="text-center">
            <p class="mb-3">
              The option "<strong>${optionToRemove.choice_label}</strong>" is currently set as the default value for this custom field.
            </p>
            <p class="mb-0">
              If you delete this option, the default value will be cleared. Do you want to continue?
            </p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isMultipleOptionType && Array.isArray(formData.default_value)) {
        const updatedValues = formData.default_value.filter(
          (val) =>
            val !== optionToRemove.choice_label &&
            val?.toString() !==
            optionToRemove.custom_field_choice_option_id?.toString()
        );
        setFormData((prev) => ({
          ...prev,
          default_value: updatedValues,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          default_value: "",
        }));
      }
    }

    if (optionToRemove.custom_field_choice_option_id && optionToRemove.custom_field_choice_option_id > 0) {
      try {
        await CustomFieldService.deleteChoiceOption(
          optionToRemove.custom_field_choice_option_id
        );
        showSuccessAlert(
          `Option "${optionToRemove.choice_label}" deleted successfully!`
        );
      } catch (error) {
        console.error("Error deleting choice option:", error);
        return;
      }
    }

    setChoiceOptions((prev) => {
      const newOptions = prev.filter((_, i) => i !== index);
      return newOptions.map((option, i) => ({
        ...option,
        display_order: i + 1,
      }));
    });
  };

  const moveChoiceOption = async (index: number, direction: "up" | "down") => {
    const newOptions = [...choiceOptions];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newOptions.length) return;

    [newOptions[index], newOptions[targetIndex]] = [
      newOptions[targetIndex],
      newOptions[index],
    ];

    const updatedOptions = newOptions.map((option, i) => ({
      ...option,
      display_order: i + 1,
    }));

    setChoiceOptions(updatedOptions);

    if (customField?.custom_field_id) {
      try {
        const updatePromises = updatedOptions
          .filter((option) => option.custom_field_choice_option_id && option.custom_field_choice_option_id > 0)
          .map((option) =>
            CustomFieldService.updateChoiceOption(
              option.custom_field_choice_option_id!,
              {
                choice_label: option.choice_label!,
                display_order: option.display_order,
              },
              customField.tenant_id
            )
          );

        await Promise.all(updatePromises);
        MySwal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Options order updated successfully!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } catch (error) {
        console.error("Error updating choice options order:", error);
      }
    }
  };

  if (dataTypesLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">
          <small className="text-muted">Loading field types...</small>
        </div>
      </div>
    );
  }

  if (!dataTypes || dataTypes.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="alert alert-warning">
          <i className="ti ti-alert-triangle me-2"></i>
          Failed to load field types. Please try again.
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="position-relative">
      {isLoadingCustomField && (
        <div
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
          style={{ zIndex: 10 }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">
              <small className="text-muted">
                Loading custom field details...
              </small>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="custom-field-form">
        <div className="row">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title mb-3">Field Information</h6>

                <div className="form-group mb-3">
                  <label className="form-label">
                    Display Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.custom_field_display_name ? "is-invalid" : ""
                      }`}
                    value={formData.custom_field_display_name || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        custom_field_display_name: e.target.value,
                      }))
                    }
                    placeholder="Enter field display name"
                  />
                  {errors.custom_field_display_name && (
                    <div className="invalid-feedback">
                      {errors.custom_field_display_name}
                    </div>
                  )}
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">
                    Related{" "}
                    {localSelectedEntities.length > 1 ? "Entities" : "Entity"}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  {!customField ? (
                    <>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {localSelectedEntities.map((entityValue) => {
                          const entity = ENTITY_OPTIONS.find(
                            (opt) => opt.value === entityValue
                          );
                          return entity ? (
                            <span
                              key={entityValue}
                              className="badge bg-primary py-2 px-3 d-flex align-items-center"
                              style={{
                                fontSize: "0.875rem",
                                width: "fit-content",
                              }}
                            >
                              <i className={`${entity.icon} me-2`}></i>
                              {entity.label}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                style={{ fontSize: "0.65rem" }}
                                onClick={() => {
                                  setLocalSelectedEntities((prev) =>
                                    prev.filter((e) => e !== entityValue)
                                  );
                                }}
                                aria-label="Remove"
                              />
                            </span>
                          ) : null;
                        })}
                      </div>
                      {ENTITY_OPTIONS.filter(
                        (opt) => !localSelectedEntities.includes(opt.value)
                      ).length > 0 && (
                          <Select
                            value={null}
                            onChange={(option: any) => {
                              if (
                                option?.value &&
                                !localSelectedEntities.includes(option.value)
                              ) {
                                setLocalSelectedEntities((prev) => [
                                  ...prev,
                                  option.value,
                                ]);
                              }
                            }}
                            options={ENTITY_OPTIONS.filter(
                              (opt) => !localSelectedEntities.includes(opt.value)
                            ).map((entity) => ({
                              value: entity.value,
                              label: entity.label,
                              icon: entity.icon,
                            }))}
                            placeholder="+ Add more entities"
                            isClearable={false}
                            className={`entity-select ${errors.related_entity_name ? "is-invalid" : ""
                              }`}
                            components={{
                              Option: (props: any) => {
                                const { data, innerProps, isFocused } = props;
                                return (
                                  <div
                                    {...innerProps}
                                    style={{
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      backgroundColor: isFocused
                                        ? "#f8f9fa"
                                        : "white",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <i className={`${data.icon} me-2`}></i>
                                    {data.label}
                                  </div>
                                );
                              },
                            }}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        )}
                      {errors.related_entity_name && (
                        <div className="invalid-feedback d-block">
                          {errors.related_entity_name}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {(() => {
                        const entity = ENTITY_OPTIONS.find(
                          (opt) =>
                            opt.value ===
                            (formData.related_entity_name || selectedEntity)
                        );
                        return entity ? (
                          <span
                            className="badge bg-primary py-2 px-3"
                            style={{ fontSize: "0.875rem" }}
                          >
                            <i className={`${entity.icon} me-2`}></i>
                            {entity.label}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>

                <div className="form-group mb-0">
                  <label className="form-label">
                    Field Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    value={
                      dataTypes?.find(
                        (type) =>
                          type.custom_field_data_type_id ===
                          formData.custom_field_data_type_id
                      )
                        ? dataTypes
                          .map((type) => ({
                            ...type,
                            value: type.custom_field_data_type_id,
                            label: type.custom_field_data_type_display_name,
                          }))
                          .find(
                            (type) =>
                              type.custom_field_data_type_id ===
                              formData.custom_field_data_type_id
                          )
                        : null
                    }
                    onChange={(option) => {
                      const newDataType = dataTypes?.find(
                        (type) =>
                          type.custom_field_data_type_id ===
                          option?.custom_field_data_type_id
                      );
                      const isNewMultipleOption =
                        newDataType?.custom_field_data_type_key ===
                        "MULTIPLE_OPTION";
                      const isNewDateRange =
                        newDataType?.custom_field_data_type_key ===
                        "DATE_RANGE";
                      const isNewTimeRange =
                        newDataType?.custom_field_data_type_key ===
                        "TIME_RANGE";
                      const isNewDateTimeRange =
                        newDataType?.custom_field_data_type_key ===
                        "DATE_TIME_RANGE";
                      const isNewDateTime =
                        newDataType?.custom_field_data_type_key === "DATETIME";
                      const isNewDependentField =
                        newDataType?.custom_field_data_type_key ===
                        "DEPENDENT_FIELD";

                      let newDefaultValue: any = "";
                      if (isNewMultipleOption) {
                        newDefaultValue = [];
                      } else if (
                        isNewDateRange ||
                        isNewTimeRange ||
                        isNewDateTimeRange
                      ) {
                        newDefaultValue = { start: "", end: "" };
                      }

                      setFormData((prev) => ({
                        ...prev,
                        custom_field_data_type_id:
                          option?.custom_field_data_type_id || 0,
                        default_value: newDefaultValue,
                      }));
                      setChoiceOptions([]);
                      setDependencyLevels([]);
                      setDependentFieldDefaultValue({});
                    }}
                    options={
                      dataTypes?.map((type) => ({
                        ...type,
                        value: type.custom_field_data_type_id,
                        label: type.custom_field_data_type_display_name,
                      })) || []
                    }
                    getOptionLabel={(option: any) =>
                      option.custom_field_data_type_display_name
                    }
                    getOptionValue={(option: any) =>
                      option.custom_field_data_type_id.toString()
                    }
                    components={{
                      Option: (props: any) => {
                        const { data, innerProps, isFocused } = props;
                        const icon = getFieldTypeIcon(data.custom_field_data_type_key);
                        return (
                          <div
                            {...innerProps}
                            style={{
                              padding: "12px 16px",
                              cursor: "pointer",
                              backgroundColor: isFocused ? "#f8f9fa" : "white",
                              borderBottom: "1px solid #e9ecef",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "flex-start" }}>
                              <i
                                className={icon}
                                style={{
                                  fontSize: "20px",
                                  marginRight: "12px",
                                  marginTop: "2px",
                                  color: "#6366f1",
                                  minWidth: "20px"
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontWeight: "500",
                                    color: "#333",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {data.custom_field_data_type_display_name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#6c757d",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {data.custom_field_data_type_description}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      },
                      SingleValue: (props: any) => {
                        const { data, innerProps, selectProps } = props;
                        const icon = getFieldTypeIcon(data.custom_field_data_type_key);
                        return (
                          <div
                            {...innerProps}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              position: "absolute",
                              top: "50%",
                              transform: "translateY(-50%)",
                              left: "12px",
                              right: "12px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              boxSizing: "border-box"
                            }}
                          >
                            <i
                              className={icon}
                              style={{
                                fontSize: "16px",
                                marginRight: "8px",
                                color: "#6366f1",
                                flexShrink: 0
                              }}
                            />
                            <span style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>
                              {data.custom_field_data_type_display_name}
                            </span>
                          </div>
                        );
                      },
                    }}
                    placeholder="Select field type"
                    className={
                      errors.custom_field_data_type_id ? "is-invalid" : ""
                    }
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "250px",
                        padding: 0,
                      }),
                      option: (base) => ({
                        ...base,
                        padding: 0,
                        backgroundColor: "transparent",
                        ":hover": {
                          backgroundColor: "transparent",
                        },
                        ":active": {
                          backgroundColor: "transparent",
                        },
                      }),
                    }}
                  />
                  {errors.custom_field_data_type_id && (
                    <div className="invalid-feedback d-block">
                      {errors.custom_field_data_type_id}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title mb-3">Additional Settings</h6>

                <div className="form-group mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.custom_field_description || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        custom_field_description: e.target.value,
                      }))
                    }
                    placeholder="Enter field description (optional)"
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">
                    Default Value{isMultipleOptionType ? "s" : ""}
                  </label>
                  {!isFieldTypeSelected ? (
                    <input
                      type="text"
                      className="form-control"
                      value=""
                      placeholder="Please select a field type first"
                      disabled
                    />
                  ) : isSwitchType ? (
                    <Select
                      value={
                        formData.default_value
                          ? {
                            value: formData.default_value,
                            label:
                              formData.default_value === "TRUE" ||
                                formData.default_value === "true" ||
                                formData.default_value === true
                                ? "On"
                                : "Off",
                          }
                          : null
                      }
                      onChange={(selectedOption) => {
                        setFormData((prev) => ({
                          ...prev,
                          default_value: selectedOption?.value || "",
                        }));
                      }}
                      options={[
                        { value: "TRUE", label: "On" },
                        { value: "FALSE", label: "Off" },
                      ]}
                      placeholder="Select default switch state (optional)"
                      isClearable
                      className="default-value-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  ) : isDateType ? (
                    <input
                      type="date"
                      className="form-control"
                      value={formData.default_value || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          default_value: e.target.value,
                        }))
                      }
                      placeholder="Select default date (optional)"
                    />
                  ) : isTimeType ? (
                    <input
                      type="time"
                      className="form-control"
                      value={formData.default_value || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          default_value: e.target.value,
                        }))
                      }
                      placeholder="Select default time (optional)"
                      step="1"
                    />
                  ) : isDateTimeType ? (
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={formData.default_value || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          default_value: e.target.value,
                        }))
                      }
                      placeholder="Select default date & time (optional)"
                      step="1"
                    />
                  ) : isDateRangeType ? (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label small">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={(() => {
                            if (
                              formData.default_value &&
                              typeof formData.default_value === "object" &&
                              formData.default_value.start
                            ) {
                              return formData.default_value.start;
                            }
                            return "";
                          })()}
                          onChange={(e) => {
                            const currentValue =
                              (typeof formData.default_value === "object" &&
                                formData.default_value) ||
                              {};
                            setFormData((prev) => ({
                              ...prev,
                              default_value: {
                                ...currentValue,
                                start: e.target.value,
                              },
                            }));
                          }}
                          placeholder="Start date"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={(() => {
                            if (
                              formData.default_value &&
                              typeof formData.default_value === "object" &&
                              formData.default_value.end
                            ) {
                              return formData.default_value.end;
                            }
                            return "";
                          })()}
                          onChange={(e) => {
                            const currentValue =
                              (typeof formData.default_value === "object" &&
                                formData.default_value) ||
                              {};
                            setFormData((prev) => ({
                              ...prev,
                              default_value: {
                                ...currentValue,
                                end: e.target.value,
                              },
                            }));
                          }}
                          placeholder="End date"
                        />
                      </div>
                    </div>
                  ) : isTimeRangeType ? (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label small">Start Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={(() => {
                            if (
                              formData.default_value &&
                              typeof formData.default_value === "object" &&
                              formData.default_value.start
                            ) {
                              return formData.default_value.start;
                            }
                            return "";
                          })()}
                          onChange={(e) => {
                            const currentValue =
                              (typeof formData.default_value === "object" &&
                                formData.default_value) ||
                              {};
                            setFormData((prev) => ({
                              ...prev,
                              default_value: {
                                ...currentValue,
                                start: e.target.value,
                              },
                            }));
                          }}
                          placeholder="Start time"
                          step="1"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">End Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={(() => {
                            if (
                              formData.default_value &&
                              typeof formData.default_value === "object" &&
                              formData.default_value.end
                            ) {
                              return formData.default_value.end;
                            }
                            return "";
                          })()}
                          onChange={(e) => {
                            const currentValue =
                              (typeof formData.default_value === "object" &&
                                formData.default_value) ||
                              {};
                            setFormData((prev) => ({
                              ...prev,
                              default_value: {
                                ...currentValue,
                                end: e.target.value,
                              },
                            }));
                          }}
                          placeholder="End time"
                          step="1"
                        />
                      </div>
                    </div>
                  ) : isDateTimeRangeType ? (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label small">
                          Start Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={(() => {
                            if (
                              formData.default_value &&
                              typeof formData.default_value === "object" &&
                              formData.default_value.start
                            ) {
                              return formData.default_value.start;
                            }
                            return "";
                          })()}
                          onChange={(e) => {
                            const currentValue =
                              (typeof formData.default_value === "object" &&
                                formData.default_value) ||
                              {};
                            setFormData((prev) => ({
                              ...prev,
                              default_value: {
                                ...currentValue,
                                start: e.target.value,
                              },
                            }));
                          }}
                          placeholder="Start date & time"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">
                          End Date & Time
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={(() => {
                            if (
                              formData.default_value &&
                              typeof formData.default_value === "object" &&
                              formData.default_value.end
                            ) {
                              return formData.default_value.end;
                            }
                            return "";
                          })()}
                          onChange={(e) => {
                            const currentValue =
                              (typeof formData.default_value === "object" &&
                                formData.default_value) ||
                              {};
                            setFormData((prev) => ({
                              ...prev,
                              default_value: {
                                ...currentValue,
                                end: e.target.value,
                              },
                            }));
                          }}
                          placeholder="End date & time"
                        />
                      </div>
                    </div>
                  ) : isDependentFieldType ? (
                    <div className="d-flex flex-column gap-2">
                      {dependencyLevels.length === 0 ? (
                        <div className="text-muted small">
                          Add dependency levels and options first
                        </div>
                      ) : (
                        dependencyLevels
                          .filter((level) => level.dependency_level?.trim())
                          .sort(
                            (a, b) =>
                              (a.display_order || 0) - (b.display_order || 0)
                          )
                          .map((level, index) => {
                            // Get the previous level to filter options
                            const previousLevel = index > 0 ? dependencyLevels
                              .filter((l) => l.dependency_level?.trim())
                              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))[index - 1] : null;

                            // Get the selected value from the previous level
                            const previousLevelValue = previousLevel
                              ? dependentFieldDefaultValue[previousLevel.dependency_level || ""]
                              : null;

                            // Get all options for this level
                            let levelOptions = choiceOptions.filter(
                              (opt) =>
                                opt.custom_field_dependency_level_id ===
                                level.custom_field_dependency_level_id &&
                                opt.choice_label?.trim()
                            );

                            // If this is not the first level and a parent is selected, filter by parent
                            if (index > 0 && previousLevelValue) {
                              const parentOption = choiceOptions.find(
                                (opt) => opt.choice_label === previousLevelValue &&
                                  opt.custom_field_dependency_level_id === previousLevel?.custom_field_dependency_level_id
                              );

                              if (parentOption) {
                                levelOptions = levelOptions.filter(
                                  (opt) => opt.parent_choice_option_id === parentOption.custom_field_choice_option_id
                                );
                              }
                            }

                            // Check if current selection is still valid
                            const currentValue = dependentFieldDefaultValue[level.dependency_level || ""];
                            const isCurrentValueValid = currentValue && levelOptions.some(
                              (opt) => opt.choice_label === currentValue
                            );

                            return (
                              <div key={index}>
                                <label className="form-label small mb-1">
                                  {level.dependency_level}
                                </label>
                                <Select
                                  value={
                                    isCurrentValueValid
                                      ? {
                                        value: currentValue,
                                        label: currentValue,
                                      }
                                      : null
                                  }
                                  onChange={(selected) => {
                                    setDependentFieldDefaultValue((prev) => {
                                      const newValue = { ...prev };

                                      // Clear all child level values when this level changes
                                      const sortedLevels = dependencyLevels
                                        .filter((l) => l.dependency_level?.trim())
                                        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

                                      sortedLevels.forEach((l, idx) => {
                                        if (idx > index) {
                                          delete newValue[l.dependency_level || ""];
                                        }
                                      });

                                      if (selected?.value) {
                                        newValue[level.dependency_level || ""] = selected.value;
                                      } else {
                                        delete newValue[level.dependency_level || ""];
                                      }

                                      return newValue;
                                    });
                                  }}
                                  options={levelOptions.map((opt) => ({
                                    value: opt.choice_label || "",
                                    label: opt.choice_label || "",
                                  }))}
                                  placeholder={`Select ${level.dependency_level}`}
                                  isClearable
                                  isDisabled={levelOptions.length === 0 || (index > 0 && !previousLevelValue)}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                  }}
                                />
                                {levelOptions.length === 0 && index > 0 && !previousLevelValue && (
                                  <small className="text-muted">
                                    Please select {previousLevel?.dependency_level} first
                                  </small>
                                )}
                                {levelOptions.length === 0 && (index === 0 || previousLevelValue) && (
                                  <small className="text-muted">
                                    No options added for this level yet
                                  </small>
                                )}
                                {levelOptions.length === 0 && index > 0 && previousLevelValue && (
                                  <small className="text-muted">
                                    No options linked to selected {previousLevel?.dependency_level}
                                  </small>
                                )}
                              </div>
                            );
                          })
                      )}
                    </div>
                  ) : isNumericType ? (
                    <input
                      type="number"
                      className="form-control"
                      value={formData.default_value || ""}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (
                          selectedDataType?.custom_field_data_type_key ===
                          "INTEGER" &&
                          value.includes(".")
                        ) {
                          setFormData((prev) => ({
                            ...prev,
                            default_value: Math.floor(
                              parseFloat(value)
                            ).toString(),
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            default_value: value,
                          }));
                        }
                      }}
                      placeholder="Enter default numeric value (optional)"
                      step={
                        selectedDataType?.custom_field_data_type_key ===
                          "DECIMAL" ||
                          selectedDataType?.custom_field_data_type_key === "FLOAT"
                          ? "0.01"
                          : "1"
                      }
                      onKeyDown={(e) => {
                        if (
                          selectedDataType?.custom_field_data_type_key ===
                          "INTEGER" &&
                          (e.key === "." || e.key === ",")
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  ) : isChoiceType ? (
                    <Select
                      isMulti={isMultipleOptionType}
                      value={(() => {
                        if (isMultipleOptionType) {
                          const defaultValues = Array.isArray(
                            formData.default_value
                          )
                            ? formData.default_value
                            : formData.default_value
                              ? [formData.default_value]
                              : [];

                          return defaultValues
                            .map((val) => {
                              if (customField) {
                                const option = availableChoiceOptions.find(
                                  (opt) =>
                                    opt.custom_field_choice_option_id?.toString() ===
                                    val?.toString() ||
                                    opt.choice_label === val
                                );
                                return option
                                  ? {
                                    value:
                                      option.custom_field_choice_option_id ||
                                      option.choice_label,
                                    label: option.choice_label,
                                  }
                                  : null;
                              } else {
                                const option = availableChoiceOptions.find(
                                  (opt) => opt.choice_label === val
                                );
                                return option
                                  ? {
                                    value: option.choice_label,
                                    label: option.choice_label,
                                  }
                                  : null;
                              }
                            })
                            .filter(Boolean);
                        } else {
                          if (customField) {
                            const option = availableChoiceOptions.find(
                              (opt) =>
                                opt.custom_field_choice_option_id?.toString() ===
                                formData.default_value?.toString() ||
                                opt.choice_label === formData.default_value
                            );
                            return option
                              ? {
                                value:
                                  option.custom_field_choice_option_id ||
                                  option.choice_label,
                                label: option.choice_label,
                              }
                              : null;
                          } else {
                            const option = availableChoiceOptions.find(
                              (opt) =>
                                opt.choice_label === formData.default_value
                            );
                            return option
                              ? {
                                value: option.choice_label,
                                label: option.choice_label,
                              }
                              : null;
                          }
                        }
                      })()}
                      onChange={(selectedOptions) => {
                        if (isMultipleOptionType) {
                          const values = Array.isArray(selectedOptions)
                            ? selectedOptions
                              .map((opt) => opt?.value)
                              .filter(Boolean)
                            : [];
                          setFormData((prev) => ({
                            ...prev,
                            default_value: values,
                          }));
                        } else {
                          const singleOption = selectedOptions as {
                            value: string | number;
                            label: string;
                          } | null;
                          setFormData((prev) => ({
                            ...prev,
                            default_value: singleOption?.value || "",
                          }));
                        }
                      }}
                      options={availableChoiceOptions.map((option) => ({
                        value: customField
                          ? option.custom_field_choice_option_id ||
                          option.choice_label
                          : option.choice_label,
                        label: option.choice_label,
                      }))}
                      placeholder={
                        availableChoiceOptions.length > 0
                          ? isMultipleOptionType
                            ? "Select default values (optional)"
                            : "Select default value (optional)"
                          : "Add choice options first"
                      }
                      isDisabled={availableChoiceOptions.length === 0}
                      isClearable
                      className="default-value-select"
                      menuPortalTarget={document.body}
                      hideSelectedOptions={false}
                      closeMenuOnSelect={!isMultipleOptionType}
                      components={
                        isMultipleOptionType
                          ? {
                            Option: ({ children, ...props }) => {
                              const { data, isSelected, selectOption } =
                                props;
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    backgroundColor: props.isFocused
                                      ? "#e9ecef"
                                      : isSelected
                                        ? "#f8f9fa"
                                        : "transparent",
                                    color: "#333",
                                    transition:
                                      "background-color 0.15s ease-in-out",
                                    borderRadius: "4px",
                                    margin: "2px 4px",
                                  }}
                                  onClick={() => selectOption(data)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => { }}
                                    className="form-check-input"
                                    style={{
                                      marginRight: "8px",
                                      pointerEvents: "none",
                                      marginTop: "0",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  <span>{children}</span>
                                </div>
                              );
                            },
                          }
                          : undefined
                      }
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                        menuList: (base) => ({
                          ...base,
                          maxHeight: "200px",
                          padding: "4px",
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: 0,
                          backgroundColor: "transparent",
                          color: "inherit",
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                          ":active": {
                            backgroundColor: "transparent",
                          },
                        }),
                      }}
                    />
                  ) : isNumericType ? (
                    <input
                      type="number"
                      className="form-control"
                      value={formData.default_value || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          default_value: e.target.value,
                        }))
                      }
                      placeholder={`Enter default ${isIntegerType ? "integer" : "number"
                        } (optional)`}
                      step={isIntegerType ? "1" : "any"}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={formData.default_value || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          default_value: e.target.value,
                        }))
                      }
                      placeholder="Enter default value (optional)"
                    />
                  )}
                </div>

                <div className="form-group mb-0">
                  <div className="form-check-group">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="is_required"
                        checked={formData.is_required || false}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_required: e.target.checked,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="is_required">
                        Required Field
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="is_active"
                        checked={formData.is_active || false}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="is_active">
                        Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isDependentFieldType && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="card-title mb-1">
                        Dependency Levels <span className="text-danger">*</span>
                        <small className="text-muted ms-2">
                          ({dependencyLevels.length}/3 levels)
                        </small>
                      </h6>
                      <p className="text-muted small mb-0">
                        Define the hierarchy levels for your dependent field
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm rounded-pill px-4"
                      onClick={addDependencyLevel}
                      disabled={dependencyLevels.length >= 3}
                      title={dependencyLevels.length >= 3 ? "Maximum 3 levels allowed" : "Add new level"}
                    >
                      + New Level
                    </button>
                  </div>

                  {errors.dependency_level && (
                    <div className="alert alert-danger small mb-3">
                      {errors.dependency_level}
                    </div>
                  )}

                  {dependencyLevels.length === 0 && (
                    <div className="text-center py-4 border rounded bg-light">
                      <i className="ti ti-hierarchy-3 fs-36 text-muted mb-2"></i>
                      <p className="text-muted mb-0">
                        Click "+ Level" to create your first dependency level
                      </p>
                    </div>
                  )}

                  {dependencyLevels.map((level, levelIndex) => {
                    const levelOptions = choiceOptions.filter(
                      (opt) =>
                        opt.custom_field_dependency_level_id ===
                        level.custom_field_dependency_level_id
                    );

                    const previousLevelOptions =
                      levelIndex > 0
                        ? choiceOptions.filter(
                          (opt) =>
                            opt.custom_field_dependency_level_id ===
                            dependencyLevels[levelIndex - 1]
                              .custom_field_dependency_level_id &&
                            opt.choice_label?.trim()
                        )
                        : [];

                    return (
                      <div
                        key={levelIndex}
                        className="mb-3 border rounded"
                        style={{
                          backgroundColor: levelIndex === 0 ? '#f8f9fa' : levelIndex === 1 ? '#f0f7ff' : '#fff5f5'
                        }}
                      >
                        <div className="p-3">
                          <div className="d-flex align-items-center mb-3">
                            <div className="me-2 mt-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary p-1"
                                onClick={() => moveDependencyLevel(levelIndex, "up")}
                                disabled={levelIndex === 0}
                                title="Move level up"
                                style={{ width: '28px', height: '28px' }}
                              >
                                <i className="ti ti-chevron-up mt-1" style={{ fontSize: '14px' }} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary p-1 mx-2"
                                onClick={() => moveDependencyLevel(levelIndex, "down")}
                                disabled={levelIndex === dependencyLevels.length - 1}
                                title="Move level down"
                                style={{ width: '28px', height: '28px' }}
                              >
                                <i className="ti ti-chevron-down mt-1" style={{ fontSize: '14px' }} />
                              </button>
                            </div>

                            <div className="flex-grow-1">
                              <div className="fw-bold mb-1 text-muted small">
                                Level {level.display_order || 0} Configuration
                              </div>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control form-control-lg fw-semibold"
                                  value={level.dependency_level}
                                  placeholder="Enter level name"
                                  onChange={(e) => updateDependencyLevel(levelIndex, "dependency_level", e.target.value)}
                                />
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm ms-4 mt-3"
                              onClick={() => removeDependencyLevel(levelIndex)}
                              title="Remove level"
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </div>

                          <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label fw-semibold mb-0">
                                Options
                              </label>
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm rounded-pill px-3"
                                onClick={() => {
                                  const newOption: Partial<CustomFieldChoiceOption> = {
                                    custom_field_choice_option_id: -(choiceOptions.length + 1),
                                    choice_label: "",
                                    display_order: choiceOptions.length + 1,
                                    custom_field_dependency_level_id:
                                      level.custom_field_dependency_level_id,
                                    parent_choice_option_id: null,
                                  };
                                  setChoiceOptions((prev) => [...prev, newOption]);
                                }}
                              >
                                + New Option
                              </button>
                            </div>

                            {levelOptions.length === 0 ? (
                              <div className="text-center py-3 border rounded bg-white">
                                <p className="text-muted small mb-0">
                                  No options added yet. Click "+ Option" to add.
                                </p>
                              </div>
                            ) : (
                              levelOptions.map((option, optionIndex) => {
                                const actualIndex = choiceOptions.findIndex(
                                  (opt) =>
                                    opt.custom_field_choice_option_id ===
                                    option.custom_field_choice_option_id
                                );
                                return (
                                  <div
                                    key={actualIndex}
                                    className="d-flex align-items-center mb-2 p-2 border rounded bg-white"
                                  >
                                    <div className="me-2">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary p-1"
                                        onClick={() => {
                                          const currentLevelOptions = choiceOptions.filter(
                                            (opt) =>
                                              opt.custom_field_dependency_level_id ===
                                              level.custom_field_dependency_level_id
                                          );
                                          const currentIndex = currentLevelOptions.findIndex(
                                            (opt) =>
                                              opt.custom_field_choice_option_id ===
                                              option.custom_field_choice_option_id
                                          );
                                          if (currentIndex > 0) {
                                            const newOptions = [...choiceOptions];
                                            const idx1 = newOptions.findIndex(
                                              (opt) =>
                                                opt.custom_field_choice_option_id ===
                                                currentLevelOptions[currentIndex]
                                                  .custom_field_choice_option_id
                                            );
                                            const idx2 = newOptions.findIndex(
                                              (opt) =>
                                                opt.custom_field_choice_option_id ===
                                                currentLevelOptions[currentIndex - 1]
                                                  .custom_field_choice_option_id
                                            );
                                            [newOptions[idx1], newOptions[idx2]] = [
                                              newOptions[idx2],
                                              newOptions[idx1],
                                            ];
                                            setChoiceOptions(
                                              newOptions.map((opt, i) => ({
                                                ...opt,
                                                display_order: i + 1,
                                              }))
                                            );
                                          }
                                        }}
                                        disabled={optionIndex === 0}
                                        title="Move up"
                                        style={{ width: '24px', height: '24px' }}
                                      >
                                        <i className="ti ti-chevron-up" style={{ fontSize: '12px' }} />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary p-1 mt-1 mx-2 mb-1"
                                        onClick={() => {
                                          const currentLevelOptions = choiceOptions.filter(
                                            (opt) =>
                                              opt.custom_field_dependency_level_id ===
                                              level.custom_field_dependency_level_id
                                          );
                                          const currentIndex = currentLevelOptions.findIndex(
                                            (opt) =>
                                              opt.custom_field_choice_option_id ===
                                              option.custom_field_choice_option_id
                                          );
                                          if (currentIndex < currentLevelOptions.length - 1) {
                                            const newOptions = [...choiceOptions];
                                            const idx1 = newOptions.findIndex(
                                              (opt) =>
                                                opt.custom_field_choice_option_id ===
                                                currentLevelOptions[currentIndex]
                                                  .custom_field_choice_option_id
                                            );
                                            const idx2 = newOptions.findIndex(
                                              (opt) =>
                                                opt.custom_field_choice_option_id ===
                                                currentLevelOptions[currentIndex + 1]
                                                  .custom_field_choice_option_id
                                            );
                                            [newOptions[idx1], newOptions[idx2]] = [
                                              newOptions[idx2],
                                              newOptions[idx1],
                                            ];
                                            setChoiceOptions(
                                              newOptions.map((opt, i) => ({
                                                ...opt,
                                                display_order: i + 1,
                                              }))
                                            );
                                          }
                                        }}
                                        disabled={optionIndex === levelOptions.length - 1}
                                        title="Move down"
                                        style={{ width: '24px', height: '24px' }}
                                      >
                                        <i className="ti ti-chevron-down" style={{ fontSize: '12px' }} />
                                      </button>
                                    </div>

                                    <div className="flex-grow-1">
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={option.choice_label}
                                        onChange={(e) =>
                                          updateChoiceOption(
                                            actualIndex,
                                            "choice_label",
                                            e.target.value
                                          )
                                        }
                                        placeholder={`Option ${optionIndex + 1}`}
                                      />
                                    </div>

                                    {levelIndex > 0 && previousLevelOptions.length > 0 && (
                                      <div className="ms-2" style={{ minWidth: '200px' }}>
                                        <Select
                                          value={
                                            option.parent_choice_option_id
                                              ? (() => {
                                                const parentOption = previousLevelOptions.find(
                                                  (opt) =>
                                                    opt.custom_field_choice_option_id ===
                                                    option.parent_choice_option_id
                                                );
                                                return parentOption
                                                  ? {
                                                    value:
                                                      parentOption.custom_field_choice_option_id,
                                                    label: parentOption.choice_label || "",
                                                  }
                                                  : null;
                                              })()
                                              : null
                                          }
                                          onChange={(selected) =>
                                            updateChoiceOption(
                                              actualIndex,
                                              "parent_choice_option_id",
                                              selected?.value || null
                                            )
                                          }
                                          options={previousLevelOptions.map((opt) => ({
                                            value: opt.custom_field_choice_option_id,
                                            label: opt.choice_label || "",
                                          }))}
                                          placeholder="Parent Option"
                                          isClearable
                                          className="react-select-container"
                                          classNamePrefix="react-select"
                                          styles={{
                                            control: (base) => ({
                                              ...base,
                                              minHeight: '38px',
                                            }),
                                            dropdownIndicator: (base) => ({
                                              ...base,
                                              padding: '4px',
                                            }),
                                          }}
                                          components={{
                                            DropdownIndicator: (props) => (
                                              <div {...props.innerProps} style={{ padding: '4px 8px' }}>
                                                <i className="ti ti-chevron-down text-success" style={{ fontSize: '14px' }}></i>
                                              </div>
                                            ),
                                          }}
                                        />
                                      </div>
                                    )}

                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm ms-2"
                                      onClick={async () => {
                                        const result = await MySwal.fire({
                                          title: "Delete Option?",
                                          text: `Are you sure you want to delete "${option.choice_label}"?`,
                                          icon: "warning",
                                          showCancelButton: true,
                                          confirmButtonColor: "#dc3545",
                                          cancelButtonColor: "#6c757d",
                                          confirmButtonText: "Yes, Delete",
                                          cancelButtonText: "Cancel",
                                        });

                                        if (result.isConfirmed) {
                                          if (
                                            option.custom_field_choice_option_id &&
                                            option.custom_field_choice_option_id > 0
                                          ) {
                                            try {
                                              await CustomFieldService.deleteChoiceOption(
                                                option.custom_field_choice_option_id
                                              );
                                              showSuccessAlert(
                                                `Option "${option.choice_label}" deleted successfully!`
                                              );
                                            } catch (error) {
                                              console.error(
                                                "Error deleting choice option:",
                                                error
                                              );
                                              return;
                                            }
                                          }

                                          setChoiceOptions((prev) => {
                                            const newOptions = prev.filter(
                                              (_, i) => i !== actualIndex
                                            );
                                            return newOptions.map((opt, i) => ({
                                              ...opt,
                                              display_order: i + 1,
                                            }));
                                          });
                                        }
                                      }}
                                      title="Remove option"
                                    >
                                      <i className="ti ti-trash" />
                                    </button>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {isChoiceType && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title mb-3">
                    Choice Options <span className="text-danger">*</span>
                    <small className="text-muted ms-2">
                      ({choiceOptions.length} options)
                    </small>
                  </h6>
                  {choiceOptions.map((option, index) => (
                    <div
                      key={index}
                      className="choice-option-item d-flex align-items-center mb-2 p-2 border rounded bg-light"
                    >
                      <div className="choice-reorder-buttons me-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => moveChoiceOption(index, "up")}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <i className="ti ti-chevron-up" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => moveChoiceOption(index, "down")}
                          disabled={index === choiceOptions.length - 1}
                          title="Move down"
                        >
                          <i className="ti ti-chevron-down" />
                        </button>
                      </div>
                      <div
                        className="me-2 text-muted small fw-bold"
                        style={{ minWidth: "20px" }}
                      >
                        {option.display_order}
                      </div>
                      <input
                        type="text"
                        className="form-control me-2"
                        value={option.choice_label}
                        onChange={(e) =>
                          updateChoiceOption(
                            index,
                            "choice_label",
                            e.target.value
                          )
                        }
                        placeholder={`Option ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={async () => {
                          const optionToRemove = choiceOptions[index];

                          const isUsedAsDefault =
                            formData.default_value &&
                            (isMultipleOptionType &&
                              Array.isArray(formData.default_value)
                              ? formData.default_value.some(
                                (val) =>
                                  val === optionToRemove.choice_label ||
                                  val?.toString() ===
                                  optionToRemove.custom_field_choice_option_id?.toString()
                              )
                              : formData.default_value ===
                              optionToRemove.choice_label ||
                              formData.default_value?.toString() ===
                              optionToRemove.custom_field_choice_option_id?.toString());

                          if (isUsedAsDefault) {
                            await removeChoiceOption(index);
                          } else if (option.custom_field_choice_option_id && option.custom_field_choice_option_id > 0) {
                            const result = await MySwal.fire({
                              title: "Delete Choice Option?",
                              html: `
                                <div class="text-center">
                                  <p class="mb-0">
                                    Are you sure you want to delete the option "<strong>${option.choice_label}</strong>"?
                                    <br />
                                    This action cannot be undone.
                                  </p>
                                </div>
                              `,
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#dc3545",
                              cancelButtonColor: "#6c757d",
                              confirmButtonText: "Yes, Delete",
                              cancelButtonText: "Cancel",
                            });

                            if (result.isConfirmed) {
                              await removeChoiceOption(index);
                            }
                          } else {
                            removeChoiceOption(index);
                          }
                        }}
                        title="Remove option"
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={addChoiceOption}
                  >
                    <i className="fa fa-plus me-1" />
                    Add Option
                  </button>
                  {errors.choice_options && (
                    <div className="text-danger small mt-1">
                      {errors.choice_options}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4 mb-2 mx-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {customField ? "Updating..." : "Creating..."}
              </>
            ) : customField ? (
              "Update Field"
            ) : (
              "Create Field"
            )}
          </button>
        </div>
      </form>


    </div>
  );
};

export default CustomFieldForm;
